/**
 * Which customers are inside a live opening right now — shift sighted, no
 * outcome yet — and the plumbing that lets the customer table show it.
 *
 * ── Why this is not React state ─────────────────────────────────────
 * The feed is `useRealtime`'s ingest path, the same hot path that plays the
 * sounds: it runs for every SSE frame and must never cause a render. So this
 * module keeps the hot set as plain module state, exactly the way `sfx.ts`
 * keeps its own, and the table reacts through two narrow channels that cost
 * nothing while nothing is happening:
 *
 *   - Rows read `isActiveHot(customerId)` once when they mount (the table is
 *     virtualised, so "mount" happens constantly as it scrolls) and paint the
 *     highlight class themselves.
 *   - The ONE mounted table registers a view. On a change it walks the ~20
 *     row elements actually in the DOM and toggles the class directly — no
 *     React reconciliation, the same pattern the realtime flash already uses —
 *     and on a fresh sighting it scrolls that customer's row into view.
 *
 * Work is deferred to `requestAnimationFrame` and coalesced, so a burst of
 * sightings in one ingest tick costs one DOM walk and one scroll (to the
 * newest), and the ingest itself only ever pays a Map write. Many customers
 * can be hot at once — every hot row wears the highlight; only the scroll is
 * singular, and it goes to the most recent sighting.
 *
 * The set is bounded twice: outcomes (`miss`/`won`) settle their customer
 * immediately, and a sweep retires anything an outcome never reached (a hunt
 * stopped mid-window) seventeen minutes after the sighting that opened it.
 */

import type { RunEvent } from '@acs/shared'

/**
 * How long a sighting stays hot when no outcome ever arrives to settle it.
 *
 * Measured from the sighting that OPENED the window — repeat sightings and
 * the claim firing do not extend it — so "highlighted for at most ten
 * minutes" is a promise about the row, not about the last line seen.
 */
const MAX_HOT_MS = 17 * 60_000

/** customerId → when it went hot (ms epoch). Presence IS the state. */
const hot = new Map<string, number>()

export interface ActiveHitView {
  /** The hot set changed — re-sync the highlight class on mounted rows. */
  onChange(): void
  /**
   * A customer JUST went hot — bring their row into view. Whether to actually
   * scroll is the view's call (the topbar's auto-scroll switch); a view that
   * declines has still had `onChange` first, so the row lights up regardless.
   */
  onHit(customerId: string): void
}

/**
 * A scroll request older than this is dropped, not delivered. The deferral
 * below rides `requestAnimationFrame`, which does not run in a hidden tab —
 * so without an age limit, switching back to a console after minutes away
 * would yank the viewport to whichever sighting happened to be pending when
 * the tab went dark. The highlight is the durable signal; the scroll is only
 * worth doing while it is news.
 */
const SCROLL_STALE_MS = 5_000

let view: ActiveHitView | null = null
let raf = 0
let pendingHit: { id: string; at: number } | null = null
let sweep: ReturnType<typeof setInterval> | null = null

/** Whether this customer is inside an ACTIVE window right now. */
export function isActiveHot(customerId: string): boolean {
  return hot.has(customerId)
}

/**
 * Paint (or unpaint) a row's ACTIVE mark — a DATA ATTRIBUTE, deliberately
 * not a class.
 *
 * React rewrites a row's whole `className` on every re-render, which strips
 * anything added imperatively; it never touches an attribute it does not
 * render. So `data-active-hit` survives selection, focus and run-state ticks
 * with no re-assertion, and — the part that matters for the animation — the
 * entrance plays exactly once per sighting instead of once per re-render.
 *
 * Three values, each a CSS state in theme.css:
 *   `on`   — fresh sighting: the entrance strike, then the steady sweep
 *   `hold` — same steady state, no strike: a hot row that (re)mounted while
 *            its window was already open, so scrolling it into view is not
 *            mistaken for a new sighting
 *   `out`  — the window closed: a short fade, then nothing
 *
 * Transitions are the only thing this writes, so calling it every frame for
 * every row costs a dataset read and, almost always, nothing else. A stale
 * `out` is left in place on purpose: the next `on` is a value CHANGE, which
 * is what restarts the entrance animation.
 */
export function markActiveHit(
  el: HTMLElement,
  isHot: boolean,
  opts: { mounting?: boolean } = {}
): void {
  const current = el.dataset['activeHit']
  if (isHot) {
    if (current === 'on' || current === 'hold') return
    el.dataset['activeHit'] = opts.mounting ? 'hold' : 'on'
  } else if (current === 'on' || current === 'hold') {
    el.dataset['activeHit'] = 'out'
  }
}

function flush(): void {
  raf = 0
  const hit = pendingHit
  pendingHit = null
  if (!view) return
  view.onChange()
  // Delivered only while it is both fresh and still true: a sighting settled
  // between request and frame — or one that waited out a hidden tab — keeps
  // its highlight rules but earns no scroll.
  if (hit !== null && Date.now() - hit.at < SCROLL_STALE_MS && hot.has(hit.id)) {
    view.onHit(hit.id)
  }
}

/**
 * Ask for a flush next frame. `hitId` non-null marks a FRESH sighting worth
 * scrolling to; several in one tick collapse to the newest, because a screen
 * can only be looking at one place and the highlight covers the rest.
 */
function schedule(hitId: string | null): void {
  if (hitId !== null) pendingHit = { id: hitId, at: Date.now() }
  if (view === null || raf !== 0) return
  raf = requestAnimationFrame(flush)
}

function ensureSweep(): void {
  if (sweep !== null) return
  sweep = setInterval(() => {
    const now = Date.now()
    let changed = false
    for (const [id, at] of hot) {
      if (now - at > MAX_HOT_MS) {
        hot.delete(id)
        changed = true
      }
    }
    if (hot.size === 0 && sweep !== null) {
      clearInterval(sweep)
      sweep = null
    }
    if (changed) schedule(null)
    // Fifteen seconds so the seventeen-minute cap lands within seconds of itself,
    // not a minute late. Runs only while something is hot, over a map that
    // holds a handful of entries: a few string compares a minute.
  }, 15_000)
}

/**
 * Feed one run event through. Called from ingest for every tagged line —
 * unconditionally, unlike the sounds, because the highlight is wanted with
 * the console muted too. Cost when the event is not one of the four run
 * events this cares about: string compares and nothing else.
 *
 * The window runs from the sighting to the OUTCOME, by request: the row
 * stays lit through the wait and through the claim, and only a `miss` or a
 * `won` puts it out — or the seventeen-minute cap, for a hunt that never reports
 * one. `active` opens it (scroll and entrance included, once); `burst` opens
 * it too when nothing is open yet, because on the no-wait flows the burst
 * line IS the sighting — but neither a repeat sighting nor the claim firing
 * inside an open window touches its clock, so the cap holds from the first
 * sighting. The next sighting after an outcome opens a fresh window.
 */
export function noteRunEvent(event: RunEvent, customerId: string): void {
  if (event === 'active' || event === 'burst') {
    if (hot.has(customerId)) return
    hot.set(customerId, Date.now())
    ensureSweep()
    schedule(customerId)
  } else if (event === 'miss' || event === 'won') {
    if (hot.delete(customerId)) schedule(null)
  }
}

/**
 * The mounted table's hookup. One at a time — a later registration replaces
 * an earlier one, which is the right answer for route changes. Returns the
 * unregister.
 */
export function registerActiveHitView(v: ActiveHitView): () => void {
  view = v
  return () => {
    if (view === v) view = null
  }
}
